/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Block } from "@xprog/prensa";
import IconButton from "components/Buttons/IconButton.index";
import ReactPlayer from "react-player";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";

export type Radio = {
  caption?: string;
  radioThumbnail?: string;
  title?: string;
  subtitle?: string;
};

const RadioWidgetWrapper = ({ data }) => {
  const [radios, setRadios] = useState<Radio[]>([]);
  const [selectedRadioIndex, setSelectedRadioIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.5);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: "y",
    loop: true,
    dragFree: false,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedRadioIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi && emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (data && data.length > 0) {
      const simplified = data.map((item: any) => {
        const firstChild = item.props.children[0];
        return {
          caption: firstChild.props.image?.caption,
          radioThumbnail: firstChild.props.image?.url,
          title: firstChild.props.title,
          subtitle: firstChild.props.subtitle,
        } as Radio;
      });

      setRadios(simplified);
    }
  }, [data]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const volumeSliderRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const updateVolume = (clientY: number) => {
      if (volumeSliderRef.current) {
        const rect = volumeSliderRef.current.getBoundingClientRect();
        const newVolume = Math.max(
          0,
          Math.min(1, 1 - (clientY - rect.top) / rect.height)
        );
        setVolume(Number(newVolume.toFixed(2)));
      }
    };

    updateVolume(event.clientY);

    const handleMouseMove = (e: MouseEvent) => updateVolume(e.clientY);
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const containerStyle: React.CSSProperties = {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    padding: "0 4%",
    alignItems: "center",
  };

  const volumeContainerStyle: React.CSSProperties = {
    flexDirection: "column",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "12px",
  };

  const volumeSliderContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  };

  const volumeSliderStyle: React.CSSProperties = {
    height: "60px",
    width: "2px",
    backgroundColor: "#e5e7eb",
    borderRadius: "9999px",
    cursor: "pointer",
    position: "relative",
  };

  const volumeFillStyle: React.CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#646464",
    borderRadius: "9999px",
    height: `${volume * 100}%`,
  };

  const volumeHandleStyle: React.CSSProperties = {
    position: "absolute",
    width: "12px",
    height: "12px",
    backgroundColor: "#646464",
    borderRadius: "50%",
    left: "-5px",
    maxHeight: 100,
    bottom: `calc(${volume * 100}% - 8px)`,
    cursor: "grab",
  };

  const carouselContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };

  const carouselStyle: React.CSSProperties = {
    position: "relative",
    minWidth: "90px",
    height: "130px",
    overflow: "hidden",
  };

  const carouselSlideStyle: React.CSSProperties = {
    flex: "0 0 70%",
    minHeight: "0",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    fontWeight: "bold",
    transition: "opacity 0.3s ease",
    cursor: "pointer",
  };

  const radioTextContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: 90,
    justifyContent: "space-between",
    paddingTop: 6,
    paddingLeft: 4,
    paddingBottom: 6,
  };

  const radioTitleStyle: React.CSSProperties = {
    fontFamily: "sans-serif",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 130,
  };

  const radioSubtitleStyle: React.CSSProperties = {
    fontFamily: "sans-serif",
    fontSize: "12px",
    fontWeight: "lighter",
    color: "#646464",
  };

  const radioPlayerContainerStyle: React.CSSProperties = {
    display: "flex",
    width: "80%",
    justifyContent: "space-between",
  };

  return (
    <div style={containerStyle}>
      <div style={carouselStyle} ref={emblaRef}>
        <div style={carouselContainerStyle}>
          {radios.map((radio, index) => (
            <div
              key={index}
              style={{
                ...carouselSlideStyle,
                opacity: index === selectedRadioIndex ? 1 : 0.3,
              }}
              onClick={() => scrollTo(index)}
              role="button"
              aria-label={`Go to slide ${index + 1}`}
              tabIndex={0}
            >
              <img
                alt="radio"
                style={{
                  backgroundColor:
                    index === selectedRadioIndex ? "#e0e0e0" : "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  margin: "-100px 0",
                  transform: `scale(${index === selectedRadioIndex ? 1 : 0.9})`,
                  transition: "all 0.3s ease",
                }}
                width={90}
                height={90}
                src={radio.radioThumbnail}
              />
            </div>
          ))}
        </div>
      </div>
      <div style={radioTextContainerStyle}>
        <div
          style={{ display: "flex", flexDirection: "column", marginLeft: 10 }}
        >
          <span style={radioTitleStyle}>
            {radios[selectedRadioIndex]?.title}
          </span>
          <span style={radioSubtitleStyle}>
            {radios[selectedRadioIndex]?.subtitle}
          </span>
        </div>
        <div style={radioPlayerContainerStyle}>
          <IconButton
            id="radio-prev"
            icon={{
              alt: "rádio anterior",
              height: "12px",
              src: "/assets/opopular/icons/IcRadioPlayerPrevious.svg",
              width: "12px",
            }}
            onClick={scrollPrev}
          />
          <IconButton
            id="radio-play"
            icon={{
              alt: "play rádio",
              height: "28px",
              width: "28px",
              src: isPlaying
                ? "/assets/opopular/icons/IcRadioPlayerPause.svg"
                : "/assets/opopular/icons/IcRadioPlayerPlay.svg",
            }}
            onClick={togglePlay}
          />
          <IconButton
            id="radio-next"
            icon={{
              alt: "próxima rádio",
              height: "12px",
              src: "/assets/opopular/icons/IcRadioPlayerNext.svg",
              width: "12px",
            }}
            onClick={scrollNext}
          />
        </div>
      </div>
      <div
        style={volumeContainerStyle}
      >
        <div style={volumeSliderContainerStyle}>
          <div
            ref={volumeSliderRef}
            style={volumeSliderStyle}
            onMouseDown={handleMouseDown}
          >
            <div style={volumeFillStyle} />
            <div style={volumeHandleStyle} />
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <Image
            height={15}
            width={15}
            src={"/assets/opopular/icons/IcRadioVolMin.svg"}
            alt="ícone de volume"
          />
        </div>
      </div>

      <Block css={{ display: "none" }}>
        <ReactPlayer
          url={radios[selectedRadioIndex]?.caption}
          playing={isPlaying}
          controls={false}
          volume={volume}
        />
      </Block>
    </div>
  );
};

export default RadioWidgetWrapper;
